import { useState } from "react";
import { Lock } from "lucide-react";
import { brl } from "@/lib/api";

/**
 * Dados do cartão — que NUNCA passam pelo nosso servidor.
 *
 * O número vai daqui direto para a Pagar.me, que devolve um token de uso único.
 * Só o token chega ao nosso backend. Essa é a razão de o formulário existir no
 * navegador em vez de um POST comum: cartão trafegando pelo nosso servidor
 * jogaria a loja inteira dentro do escopo pesado do PCI-DSS, com auditoria,
 * segregação de rede e retenção de log — para guardar um dado que não queremos.
 *
 * Nada aqui é enviado ao backend, nem fica em estado global: o componente
 * entrega o token e esquece o resto.
 */

const soDigitos = (v) => String(v || "").replace(/\D/g, "");

/** 4 em 4, como está impresso no plástico — é assim que a pessoa confere. */
const mascaraNumero = (v) => soDigitos(v).slice(0, 19).replace(/(\d{4})(?=\d)/g, "$1 ").trim();
const mascaraValidade = (v) => {
  const d = soDigitos(v).slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

export default function CardFields({ valor, parcelas, onParcelasChange, onChange }) {
  const [cartao, setCartao] = useState({ number: "", holder_name: "", exp: "", cvv: "" });

  const set = (k, mascara) => (e) => {
    const proximo = { ...cartao, [k]: mascara ? mascara(e.target.value) : e.target.value };
    setCartao(proximo);
    onChange(proximo);
  };

  /* Parcelas de 1 a 12, com o valor de cada uma no rótulo.
     Sem o valor por parcela a pessoa escolhe no escuro — "6x" não diz nada, e
     "6x de R$ 82,42" é a informação que decide. */
  const opcoes = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div className="mt-4 space-y-3 border-t border-slate-100 pt-4" data-testid="checkout-card-fields">
      <div>
        <label htmlFor="card-number" className="text-eyebrow uppercase text-slate-500 block mb-1.5">Número do cartão</label>
        <input
          id="card-number"
          aria-label="Número do cartão"
          value={cartao.number}
          onChange={set("number", mascaraNumero)}
          inputMode="numeric"
          autoComplete="cc-number"
          placeholder="0000 0000 0000 0000"
          data-testid="card-number"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-base font-mono tracking-wide"
        />
      </div>

      <div>
        <label htmlFor="card-holder" className="text-eyebrow uppercase text-slate-500 block mb-1.5">Nome impresso no cartão</label>
        <input
          id="card-holder"
          aria-label="Nome impresso no cartão"
          value={cartao.holder_name}
          onChange={(e) => set("holder_name")({ target: { value: e.target.value.toUpperCase() } })}
          autoComplete="cc-name"
          placeholder="COMO ESTÁ NO CARTÃO"
          data-testid="card-holder"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-base uppercase"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="card-exp" className="text-eyebrow uppercase text-slate-500 block mb-1.5">Validade</label>
          <input
            id="card-exp"
          aria-label="Validade do cartão, mês e ano"
            value={cartao.exp}
            onChange={set("exp", mascaraValidade)}
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder="MM/AA"
            data-testid="card-exp"
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-base font-mono"
          />
        </div>
        <div>
          <label htmlFor="card-cvv" className="text-eyebrow uppercase text-slate-500 block mb-1.5">Código de segurança</label>
          <input
            id="card-cvv"
          aria-label="Código de segurança do cartão"
            value={cartao.cvv}
            onChange={set("cvv", (v) => soDigitos(v).slice(0, 4))}
            inputMode="numeric"
            autoComplete="cc-csc"
            placeholder="CVV"
            data-testid="card-cvv"
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-base font-mono"
          />
        </div>
      </div>

      <div>
        <label htmlFor="card-installments" className="text-eyebrow uppercase text-slate-500 block mb-1.5">Parcelas</label>
        <select
          id="card-installments"
          value={parcelas}
          onChange={(e) => onParcelasChange(Number(e.target.value))}
          data-testid="card-installments"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-base bg-white"
        >
          {opcoes.map((n) => (
            <option key={n} value={n}>
              {n}x de {brl(valor / n)}{n > 1 ? " sem juros" : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Onde o dado vai — e onde não vai. É a pergunta que a pessoa faz antes
          de digitar o número, e responder aqui vale mais que qualquer selo. */}
      <p className="text-xs text-slate-500 flex items-start gap-1.5 leading-relaxed">
        <Lock size={13} className="text-emerald-600 shrink-0 mt-0.5" aria-hidden />
        Os dados do cartão vão direto para a operadora de pagamento. A loja não recebe
        nem armazena o número do seu cartão.
      </p>
    </div>
  );
}
